"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface FieldDef {
  key: string;
  label: string;
  type?: "text" | "money" | "date" | "json";
}

interface DetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  row: Record<string, unknown> | null;
  fields: FieldDef[];
  rowJson?: string | null;
}

function formatValue(val: unknown, type: string): string {
  if (val === null || val === undefined) return "-";
  if (type === "money") return `¥${Number(val).toLocaleString("zh-CN", { minimumFractionDigits: 2 })}`;
  if (type === "date") return String(val).slice(0, 10);
  return String(val);
}

function formatJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

export function DetailDialog({ open, onOpenChange, title, row, fields, rowJson }: DetailDialogProps) {
  if (!row) return null;

  const hasJson = rowJson && rowJson.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[80vw] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>查看记录详细信息</DialogDescription>
        </DialogHeader>

        {hasJson ? (
          <Tabs defaultValue="info">
            <TabsList>
              <TabsTrigger value="info">基本信息</TabsTrigger>
              <TabsTrigger value="json">JSON 数据</TabsTrigger>
            </TabsList>
            <TabsContent value="info">
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {fields.map((f) => (
                  <div key={f.key} className="flex justify-between border-b border-zinc-100 py-1">
                    <span className="text-zinc-500 shrink-0">{f.label}</span>
                    <span className="text-zinc-900 text-right ml-4">
                      {formatValue(row[f.key], f.type ?? "text")}
                    </span>
                  </div>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="json">
              <pre className="bg-zinc-900 text-zinc-100 rounded-lg p-4 text-xs overflow-x-auto whitespace-pre-wrap">
                {formatJson(rowJson)}
              </pre>
            </TabsContent>
          </Tabs>
        ) : (
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {fields.map((f) => (
              <div key={f.key} className="flex justify-between border-b border-zinc-100 py-1">
                <span className="text-zinc-500 shrink-0">{f.label}</span>
                <span className="text-zinc-900 text-right ml-4">
                  {formatValue(row[f.key], f.type ?? "text")}
                </span>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}